// The client acknowledgement of a document (SEP-293 sections 9, 10 and 11).
//
// The client opens each document of the pack of their service type, then
// ticks "I have read and understood this document". The client does this on
// the application, before they submit it, and the submission publishes each
// copy already acknowledged. A new version of a document that the firm
// publishes later goes to the portal, and the client acknowledges it there.
// One row records one version of one document (`PortalDocumentAcknowledgement`
// in the wealth ontology). This module holds the wire shapes, the words of the
// statement and the pure fold. The application, the portal, the document
// panel of the case, the board chip and the Signing block read the same words.
//
// This is the PORTAL acknowledgement. It is not the signed copy that the
// register of provided documents records (`acknowledgementReceivedAt`).

import { z } from 'zod';

import {
    PROVIDED_DOCUMENT_CATALOGUE,
    deliveryPointOf,
    providedDocumentCatalogueItem,
    type ProvidedDocumentEntryDto,
} from './onboarding-document-registers';

/** Where one version of one document stands for the client. */
export const portalAcknowledgementStatusSchema = z.enum([
    'not_opened',
    'opened',
    'acknowledged',
    'superseded',
]);
export type PortalAcknowledgementStatus = z.infer<typeof portalAcknowledgementStatusSchema>;

/** The code of each statement wording that a client can tick. */
export const portalAcknowledgementStatementCodeSchema = z.enum(['read_and_understood_v1']);
export type PortalAcknowledgementStatementCode = z.infer<
    typeof portalAcknowledgementStatementCodeSchema
>;

/**
 * The words of each statement. The card of SEP-293 section 9 gives them. The
 * code is a value, so the map is built from pairs and holds no snake_case key.
 */
export const PORTAL_ACKNOWLEDGEMENT_STATEMENTS = Object.freeze(
    Object.fromEntries([['read_and_understood_v1', 'I have read and understood this document']]),
) as Readonly<Record<PortalAcknowledgementStatementCode, string>>;

/** The statement that the portal shows now. */
export const CURRENT_PORTAL_ACKNOWLEDGEMENT_STATEMENT: PortalAcknowledgementStatementCode =
    'read_and_understood_v1';

/** The acknowledgement state of one document in the portal library. */
export const portalDocumentAcknowledgementViewSchema = z.object({
    /** The portal shows the Open and acknowledge controls. */
    requested: z.boolean(),
    required: z.boolean(),
    versionNumber: z.number().int().nullable(),
    versionLabel: z.string().nullable(),
    status: portalAcknowledgementStatusSchema,
    openedAt: z.string().nullable(),
    acknowledgedAt: z.string().nullable(),
    /** The client acknowledged an earlier version, and not this one. */
    revised: z.boolean(),
});
export type PortalDocumentAcknowledgementView = z.infer<
    typeof portalDocumentAcknowledgementViewSchema
>;

/** The body of `POST /api/portal/documents/:documentId/acknowledge`. */
export const acknowledgePortalDocumentRequestSchema = z.object({
    statementCode: portalAcknowledgementStatementCodeSchema,
});
export type AcknowledgePortalDocumentRequest = z.infer<
    typeof acknowledgePortalDocumentRequestSchema
>;

/** One acknowledgement row, as a staff reader of a case reads it. */
export const portalDocumentAcknowledgementDtoSchema = z.object({
    id: z.string(),
    caseId: z.string().nullable(),
    portalDocumentId: z.string(),
    documentCode: z.string().nullable(),
    documentTitle: z.string(),
    firmDocumentId: z.string().nullable(),
    versionNumber: z.number().int(),
    versionLabel: z.string().nullable(),
    required: z.boolean(),
    status: portalAcknowledgementStatusSchema,
    publishedAt: z.string(),
    openedAt: z.string().nullable(),
    acknowledgedAt: z.string().nullable(),
    supersededAt: z.string().nullable(),
});
export type PortalDocumentAcknowledgementDto = z.infer<
    typeof portalDocumentAcknowledgementDtoSchema
>;

// ---------------------------------------------------------------------------
// The fold of one case (SEP-293 sections 10 and 11)
// ---------------------------------------------------------------------------

/** Where one document of a case stands for the client. */
export const caseDocumentAcknowledgementStateSchema = z.enum([
    'not_opened',
    'opened',
    'acknowledged',
]);

/** What the client must do next for one document. */
export const caseDocumentOutstandingActionSchema = z.enum([
    'client_to_open',
    'client_to_acknowledge',
]);

/** One document of the panel of the IM and the EA. */
export const caseDocumentAcknowledgementLineSchema = z.object({
    documentCode: z.string(),
    documentLabel: z.string(),
    required: z.boolean(),
    state: caseDocumentAcknowledgementStateSchema,
    portalDocumentId: z.string().nullable(),
    versionNumber: z.number().int().nullable(),
    versionLabel: z.string().nullable(),
    openedAt: z.string().nullable(),
    acknowledgedAt: z.string().nullable(),
    /** The client acknowledged an earlier version, and not the live version. */
    revised: z.boolean(),
    /** What must happen next. Null when nothing is open. */
    outstandingAction: caseDocumentOutstandingActionSchema.nullable(),
});
export type CaseDocumentAcknowledgementLine = z.infer<typeof caseDocumentAcknowledgementLineSchema>;

/** The counts that the case DTO and the board chip carry. */
export const caseDocumentAcknowledgementCountsSchema = z.object({
    requiredTotal: z.number().int(),
    acknowledged: z.number().int(),
    outstanding: z.number().int(),
    /** The labels of the required documents that are not acknowledged, revised ones apart. */
    unacknowledged: z.array(z.string()),
    /** The labels of the required documents whose live version is revised and not acknowledged. */
    revised: z.array(z.string()),
});
export type CaseDocumentAcknowledgementCounts = z.infer<
    typeof caseDocumentAcknowledgementCountsSchema
>;

/** The answer of `GET /api/onboarding/cases/:id/document-acknowledgements`. */
export const caseDocumentAcknowledgementsResponseSchema = z.object({
    summary: caseDocumentAcknowledgementCountsSchema,
    lines: z.array(caseDocumentAcknowledgementLineSchema),
});
export type CaseDocumentAcknowledgementsResponse = z.infer<
    typeof caseDocumentAcknowledgementsResponseSchema
>;

/** The register fields that the fold reads. */
export type AcknowledgementRegisterRow = Pick<
    ProvidedDocumentEntryDto,
    'documentCode' | 'documentLabel' | 'mandatory' | 'provided'
>;

/** The acknowledgement fields that the fold reads. */
export type AcknowledgementFoldRow = Pick<
    PortalDocumentAcknowledgementDto,
    | 'portalDocumentId'
    | 'documentCode'
    | 'documentTitle'
    | 'versionNumber'
    | 'versionLabel'
    | 'required'
    | 'status'
    | 'openedAt'
    | 'acknowledgedAt'
    | 'supersededAt'
>;

export interface DocumentAcknowledgementSummary extends CaseDocumentAcknowledgementCounts {
    readonly lines: CaseDocumentAcknowledgementLine[];
}

/**
 * Fold the register and every acknowledgement row of ONE case into the lines
 * of the document panel and the counts of the Signing block.
 *
 * THE ROWS GIVE THE REQUIRED SET. The service type that the client chose gives
 * the pack, and the publication writes one row for each document of the pack
 * (`required` is the mandatory flag of the catalogue). Thus a case asks for
 * the documents of its own service type, and no catalogue list stands in for
 * the pack. A case that holds no row asks for nothing: the firm gave the
 * documents by another route, and the register of provided documents records
 * that route.
 *
 * - A document is required when its live row is `required`, the firm gives it
 *   at the application, and the register does not mark it `na`. The Schedule
 *   of Charges goes out with the final pack, after the approval, so it cannot
 *   hold the move into Signing.
 * - The live version of a code is its newest row that is not superseded.
 * - A line is `revised` when the client acknowledged an earlier version and
 *   not the live version. It counts apart, so the block can say so.
 * - A document that is not required shows as a line, and it never counts.
 */
export function summariseDocumentAcknowledgements(input: {
    provided: ReadonlyArray<AcknowledgementRegisterRow>;
    acknowledgements: ReadonlyArray<AcknowledgementFoldRow>;
}): DocumentAcknowledgementSummary {
    const byCode = new Map(input.provided.map((row) => [row.documentCode, row]));
    const rowsOf = new Map<string, AcknowledgementFoldRow[]>();
    for (const row of input.acknowledgements) {
        if (!row.documentCode) continue;
        const list = rowsOf.get(row.documentCode) ?? [];
        list.push(row);
        rowsOf.set(row.documentCode, list);
    }

    const lines: CaseDocumentAcknowledgementLine[] = [...rowsOf.entries()].map(([code, rows]) => {
        const newest = [...rows].sort((a, b) => b.versionNumber - a.versionNumber);
        const live = newest.find((r) => r.status !== 'superseded' && !r.supersededAt) ?? newest[0];
        const isRequired =
            Boolean(live?.required) &&
            deliveryPointOf(code) === 'application' &&
            byCode.get(code)?.provided !== 'na';
        const label =
            byCode.get(code)?.documentLabel ||
            providedDocumentCatalogueItem(code)?.label ||
            live?.documentTitle ||
            code;
        const state: CaseDocumentAcknowledgementLine['state'] =
            !live || live.status === 'superseded' ? 'not_opened' : live.status;
        const revised =
            Boolean(live) &&
            state !== 'acknowledged' &&
            rows.some(
                (r) =>
                    r !== live &&
                    r.acknowledgedAt !== null &&
                    r.versionNumber < (live?.versionNumber ?? 0),
            );
        return {
            documentCode: code,
            documentLabel: label,
            required: isRequired,
            state,
            portalDocumentId: live?.portalDocumentId ?? null,
            versionNumber: live?.versionNumber ?? null,
            versionLabel: live?.versionLabel ?? null,
            openedAt: live?.openedAt ?? null,
            acknowledgedAt: live?.acknowledgedAt ?? null,
            revised,
            outstandingAction:
                state === 'not_opened'
                    ? 'client_to_open'
                    : state === 'opened'
                      ? 'client_to_acknowledge'
                      : null,
        };
    });
    // The order of the catalogue, then the codes that the firm added.
    const order = (code: string) => {
        const index = PROVIDED_DOCUMENT_CATALOGUE.findIndex((item) => item.code === code);
        return index === -1 ? PROVIDED_DOCUMENT_CATALOGUE.length : index;
    };
    lines.sort((a, b) => order(a.documentCode) - order(b.documentCode));

    const requiredLines = lines.filter((line) => line.required);
    const acknowledged = requiredLines.filter((line) => line.state === 'acknowledged').length;
    return {
        requiredTotal: requiredLines.length,
        acknowledged,
        outstanding: requiredLines.length - acknowledged,
        unacknowledged: requiredLines
            .filter((line) => line.state !== 'acknowledged' && !line.revised)
            .map((line) => line.documentLabel),
        revised: requiredLines
            .filter((line) => line.state !== 'acknowledged' && line.revised)
            .map((line) => line.documentLabel),
        lines,
    };
}

/** The counts of a summary, for the case DTO and the board chip. */
export function documentAcknowledgementCounts(
    summary: DocumentAcknowledgementSummary,
): CaseDocumentAcknowledgementCounts {
    return {
        requiredTotal: summary.requiredTotal,
        acknowledged: summary.acknowledged,
        outstanding: summary.outstanding,
        unacknowledged: [...summary.unacknowledged],
        revised: [...summary.revised],
    };
}

const NUMBER_WORDS = [
    'Zero',
    'One',
    'Two',
    'Three',
    'Four',
    'Five',
    'Six',
    'Seven',
    'Eight',
    'Nine',
    'Ten',
];

function countWord(n: number): string {
    return NUMBER_WORDS[n] ?? String(n);
}

/** One reason of the document condition of the Signing block. */
export interface DocumentSigningBlocker {
    readonly code: 'documents_unacknowledged' | 'document_revision_unacknowledged';
    readonly message: string;
    readonly documents: ReadonlyArray<string>;
}

/**
 * The document condition of the Signing block (SEP-293 section 11): each
 * required document is opened and acknowledged at the current version. The
 * messages are the words of the card: "Two required documents remain
 * unacknowledged." and "A revised document requires acknowledgement."
 */
export function documentSigningBlockers(
    counts: Pick<CaseDocumentAcknowledgementCounts, 'unacknowledged' | 'revised'>,
): DocumentSigningBlocker[] {
    const out: DocumentSigningBlocker[] = [];
    const n = counts.unacknowledged.length;
    if (n > 0) {
        out.push({
            code: 'documents_unacknowledged',
            message:
                n === 1
                    ? 'One required document remains unacknowledged.'
                    : `${countWord(n)} required documents remain unacknowledged.`,
            documents: [...counts.unacknowledged],
        });
    }
    const r = counts.revised.length;
    if (r > 0) {
        out.push({
            code: 'document_revision_unacknowledged',
            message:
                r === 1
                    ? 'A revised document requires acknowledgement.'
                    : `${countWord(r)} revised documents require acknowledgement.`,
            documents: [...counts.revised],
        });
    }
    return out;
}
